import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, Mail, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { api } from "../lib/api";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "checking", email: null, fulfilled: false });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", email: null, fulfilled: false });
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid" && data.fulfilled) {
          setState({ status: "paid", email: data.email, fulfilled: true });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", email: null, fulfilled: false });
          return;
        }
        if (attempts >= 12) {
          setState({ status: "timeout", email: data.email, fulfilled: false });
          return;
        }
        setTimeout(poll, 2500);
      } catch (e) {
        if (attempts >= 12) {
          setState({ status: "error", email: null, fulfilled: false });
          return;
        }
        setTimeout(poll, 2500);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="App" data-testid="checkout-success-page">
      <Nav />
      <section className="min-h-[70vh] flex items-center justify-center py-20">
        <div className="max-w-xl mx-auto px-6 md:px-12 text-center">
          {state.status === "checking" && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
              <Loader2 className="mx-auto animate-spin mb-6" size={28} style={{ color: "var(--mek-accent)" }} />
              <p className="eyebrow mb-4">Confirming your purchase</p>
              <h1 className="h-display text-4xl md:text-5xl mb-4">One moment.</h1>
              <p className="text-prose">We're confirming the payment and getting your kit ready.</p>
            </motion.div>
          )}
          {state.status === "paid" && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="checkout-success-content">
              <Mail className="mx-auto mb-6" size={32} style={{ color: "var(--mek-accent)" }} />
              <p className="eyebrow mb-4">You're in</p>
              <h1 className="h-display text-4xl md:text-5xl mb-6">Welcome. Check your inbox.</h1>
              <p className="text-prose mb-2 text-lg">
                We just sent your access link to{" "}
                <span style={{ color: "var(--mek-text)" }}>{state.email}</span>.
              </p>
              <p className="text-prose mb-10">
                Open the email and click the button to unlock your library. The link works for 24 hours — request a new one any time.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button onClick={() => navigate("/access")} className="btn-primary" data-testid="success-resend-btn">
                  Get a new link
                  <ArrowRight size={16} />
                </button>
                <button onClick={() => navigate("/library")} className="btn-secondary" data-testid="success-go-library">
                  Already verified? Open library
                </button>
              </div>
            </motion.div>
          )}
          {(state.status === "expired" || state.status === "error" || state.status === "timeout") && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="checkout-error-content">
              <p className="eyebrow mb-4">Hmm</p>
              <h1 className="h-display text-4xl mb-6">Something didn't quite settle.</h1>
              <p className="text-prose mb-8">
                {state.status === "expired"
                  ? "Your checkout session expired. No charge was made — please try again."
                  : "We couldn't confirm the payment status from here. If you've been charged, request a new access link below."}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button onClick={() => navigate("/")} className="btn-secondary">Back home</button>
                <button onClick={() => navigate("/access")} className="btn-primary">Get an access link</button>
              </div>
            </motion.div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
